import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import ResizeObserver from 'resize-observer-polyfill';
import { useTranslation } from 'common/i18n/hooks';
import { ButtonReact } from 'common/components/button';
import { ButtonSize, ButtonTheme } from 'common/components/button/constants';
import { SvgIconReact } from 'common/components/svg-icon';
import { Tooltip } from 'common/components/tooltip/tooltip';
import { Placement } from 'common/components/tooltip/types';
import { TooltipReact } from '../tooltip';
import { SvgIconType } from '../svg-icon/types';
import { NarrowMode, ToggleOption, ToggleSize, ToggleTheme } from './types';
import styles from './index.module.styl';
type ToggleProps = {
  elref?: string;
  selected?: string;
  size?: ToggleSize;
  theme?: ToggleTheme;
  options?: ToggleOption[];
  inline?: boolean;
  iconOnlyWidthThreshold?: number;
  onSelect?: (value: string) => void;
  onHovered?: (value: string) => void;
  className?: string;
};
export function Toggle(props: ToggleProps) {
  const {
    selected,
    size = ToggleSize.Medium,
    theme = ToggleTheme.SegmentedController,
    options = [],
    inline = false,
    iconOnlyWidthThreshold,
    onSelect,
    onHovered,
    className
  } = props;
  const {
    print
  } = useTranslation();
  const [narrowMode, setNarrowMode] = useState<NarrowMode | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const pendingHoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Resize observer logic for icon-only mode
  useEffect(() => {
    if (!iconOnlyWidthThreshold || !wrapperRef.current) {
      return () => {};
    }
    resizeObserverRef.current = new ResizeObserver(entries => {
      const {
        width
      } = entries[0].contentRect;
      const newNarrowMode = width <= iconOnlyWidthThreshold ? NarrowMode.OnlyIcons : null;
      setNarrowMode(newNarrowMode);
    });
    resizeObserverRef.current.observe(wrapperRef.current);
    return () => {
      resizeObserverRef.current?.disconnect();
    };
  }, [iconOnlyWidthThreshold]);

  // Cleanup hover timeout on unmount
  useEffect(() => {
    return () => {
      if (pendingHoverTimeoutRef.current) {
        clearTimeout(pendingHoverTimeoutRef.current);
      }
    };
  }, []);
  const handleSelectOption = (value: string) => {
    if (selected !== value) {
      onSelect?.(value);
    }
  };
  const handleHovered = (value: string) => {
    if (selected !== value && onHovered) {
      // Clear any pending hover event
      if (pendingHoverTimeoutRef.current) {
        clearTimeout(pendingHoverTimeoutRef.current);
      }
      pendingHoverTimeoutRef.current = setTimeout(() => {
        onHovered(value);
        pendingHoverTimeoutRef.current = null;
      }, 100);
    }
  };
  const cancelHover = () => {
    if (pendingHoverTimeoutRef.current) {
      clearTimeout(pendingHoverTimeoutRef.current);
      pendingHoverTimeoutRef.current = null;
    }
  };
  const selectedIndex = options.findIndex(toggleOption => toggleOption.value === selected);
  const hideTooltip = theme === ToggleTheme.TabBarHeavy && narrowMode !== NarrowMode.OnlyIcons;
  return <div className={clsx(styles.mpToggleWrapper, className, {
    [styles.themeTabbar]: theme === ToggleTheme.TabBar,
    [styles.themeTabbarHeavy]: theme === ToggleTheme.TabBarHeavy
  })} data-sentry-component="Toggle" data-sentry-source-file="index.tsx">
      <div elref={props.elref} ref={wrapperRef} className={clsx(styles.mpToggle, {
      [styles.toggleLarge]: size === ToggleSize.Large,
      [styles.toggleSmall]: size === ToggleSize.Small,
      [styles.toggleInline]: inline,
      [styles.themeTabbar]: theme === ToggleTheme.TabBar,
      [styles.themeTabbarBorderless]: theme === ToggleTheme.TabBarBorderless,
      [styles.themeTabbarBorderlessCompact]: theme === ToggleTheme.TabBarBorderlessCompact,
      [styles.themeTabbarLite]: theme === ToggleTheme.TabBarLite,
      [styles.themeTabbarHeavy]: theme === ToggleTheme.TabBarHeavy,
      [styles.themeSegcon]: theme === ToggleTheme.SegmentedController,
      [styles.themeLargeFont]: theme === ToggleTheme.TabBarLargeFont
    })}>
        {options.map((toggleOption, index) => {
        const isSelected = index === selectedIndex;
        const beforeSelected = index === selectedIndex - 1;
        const {
          icon,
          iconColor,
          textColor,
          value,
          disabled,
          tooltip,
          tooltipLabel,
          href
        } = toggleOption;
        const label = print(toggleOption.label);
        const showLabel = label.length && narrowMode !== NarrowMode.OnlyIcons;
        const hasTextAndIcon = Boolean(icon && showLabel);
        let tooltipEl: React.ReactNode = null;
        if (tooltip) {
          tooltipEl = <TooltipReact {...tooltip} />;
        } else if (tooltipLabel) {
          tooltipEl = <TooltipReact placement={Placement.Top} text={tooltipLabel} />;
        }
        return theme === ToggleTheme.SegmentedController ? <div key={value} className={clsx(styles.mpToggleOption, {
          [styles.selected]: isSelected
        })} aria-selected={isSelected}>
              <ButtonReact class={styles.toggleButton} theme={ButtonTheme.Tertiary} size={size as TODO as ButtonSize} icon={icon} disabled={disabled} not-focusable href={href} onClick={() => !disabled && handleSelectOption(value)}>
                {label}
              </ButtonReact>
              {tooltipEl}
            </div> : <a key={value} className={clsx(styles.mpToggleOption, {
          [styles.mpToggleSelected]: isSelected,
          [styles.beforeSelected]: beforeSelected,
          [styles.mpToggleDisabled]: disabled,
          [styles.mpToggleTextAndIcon]: hasTextAndIcon
        })} style={{
          '--selected-toggle-icon-color': iconColor,
          '--selected-toggle-text-color': textColor
        }} onClick={() => !disabled && handleSelectOption(value)} onMouseEnter={() => handleHovered(value)} onMouseLeave={() => cancelHover()} href={href} role='tab' aria-selected={isSelected}>
              <Tooltip placement={Placement.Top} content={print(tooltipLabel)} enabled={!hideTooltip && !!tooltipLabel}>
                <>
                  <div className={styles.container}>
                    {icon ? <div className={styles.iconContainer} data-icon={icon}>
                        <SvgIconReact className={styles.icon} icon={icon as SvgIconType} />
                      </div> : null}
                    {showLabel ? label : null}
                  </div>
                  <div className={styles.highlightBar} />
                </>
              </Tooltip>
            </a>;
      })}
      </div>
    </div>;
}